import React from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Fab,
  Chip,
} from '@mui/material';
import {
  Add,
  EmojiEvents,
  FilterList,
} from '@mui/icons-material';
import { useTournaments } from '@/hooks/useApi';
import { useAdminMode } from '@/hooks/useManage';
import { TournamentCard, SportFilter, LoadingState, ErrorState } from '@/components';
import { BOTTOM_NAV_OFFSET } from '@/components/BottomNav';
import type { SportType } from '@/types';
import {
  parseTournamentListSearch,
  serializeTournamentListSearch,
  tournamentListHeadingSuffix,
  TOURNAMENT_LIST_TAB_LABELS,
  type TournamentListTab,
} from '@/utils/tournamentListSearch';

export const TournamentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseTournamentListSearch(searchParams);
  const activeTab: TournamentListTab = filters.status ?? 'all';
  const selectedSport = filters.sportType ?? null;
  const headingSuffix = tournamentListHeadingSuffix(filters);
  const { adminMode, create } = useAdminMode();
  const mine = searchParams.get('mine') === '1';
  const organizationId = searchParams.get('organizationId') ?? undefined;

  const replaceFilters = (next: { sportType?: SportType | null; status?: TournamentListTab; mine?: boolean }) => {
    const params = serializeTournamentListSearch({
      sportType: next.sportType === undefined ? filters.sportType : (next.sportType ?? undefined),
      status: next.status === undefined ? filters.status : (next.status === 'all' ? undefined : next.status),
    });
    if (next.mine ?? mine) params.set('mine', '1');
    if (organizationId) params.set('organizationId', organizationId);
    setSearchParams(params, { replace: true });
  };

  const {
    data: tournaments,
    isLoading,
    error,
    refetch,
  } = useTournaments({ limit: 50, sportType: selectedSport || undefined, mine: mine || undefined, organizationId });

  const filterTournaments = () => {
    if (!tournaments?.data) return [];

    let filtered = tournaments.data;

    switch (activeTab) {
      case 'ongoing':
        filtered = filtered.filter((t) => t.status === 'ongoing');
        break;
      case 'upcoming':
        filtered = filtered.filter((t) => t.status === 'published' || t.status === 'draft');
        break;
      case 'completed':
        filtered = filtered.filter((t) => t.status === 'completed' || t.status === 'archived');
        break;
      case 'draft':
        filtered = filtered.filter((t) => t.status === 'draft');
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredTournaments = filterTournaments();

  const getStatusCount = (status: TournamentListTab): number => {
    if (!tournaments?.data) return 0;
    const base = selectedSport
      ? tournaments.data.filter((t) => t.sportType === selectedSport)
      : tournaments.data;

    switch (status) {
      case 'ongoing':
        return base.filter((t) => t.status === 'ongoing').length;
      case 'upcoming':
        return base.filter((t) => t.status === 'published' || t.status === 'draft').length;
      case 'completed':
        return base.filter((t) => t.status === 'completed' || t.status === 'archived').length;
      case 'draft':
        return base.filter((t) => t.status === 'draft').length;
      default:
        return base.length;
    }
  };

  return (
    <Box sx={{ pb: 10 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <EmojiEvents color="primary" sx={{ fontSize: 28, alignSelf: 'center' }} />
          <Typography variant="h5" fontWeight="bold">
            Турниры
          </Typography>
          {headingSuffix && (
            <Typography variant="subtitle1" color="text.secondary">
              {headingSuffix}
            </Typography>
          )}
        </Box>
        {(adminMode || mine) && (
          <Chip
            icon={<FilterList />}
            label="Мои"
            color={mine ? 'manage' : 'default'}
            variant={mine ? 'filled' : 'outlined'}
            onClick={() => replaceFilters({ mine: !mine })}
            sx={{ height: 36 }}
          />
        )}
      </Box>

      {/* Sport Filter */}
      <SportFilter
        selectedSport={selectedSport}
        onSelectSport={(sport) => replaceFilters({ sportType: sport })}
      />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs
          value={activeTab}
          onChange={(_, value: TournamentListTab) => replaceFilters({ status: value })}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-flexContainer': {
              px: 1,
            },
          }}
        >
          <Tab
            value="all"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>Все</span>
                <Chip
                  label={getStatusCount('all')}
                  size="small"
                  sx={{ height: 18, fontSize: '0.7rem' }}
                />
              </Box>
            }
            sx={{ minHeight: 48, textTransform: 'none' }}
          />
          <Tab
            value="ongoing"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{TOURNAMENT_LIST_TAB_LABELS.ongoing}</span>
                <Chip
                  label={getStatusCount('ongoing')}
                  size="small"
                  sx={{ height: 18, fontSize: '0.7rem' }}
                />
              </Box>
            }
            sx={{ minHeight: 48, textTransform: 'none' }}
          />
          <Tab
            value="upcoming"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{TOURNAMENT_LIST_TAB_LABELS.upcoming}</span>
                <Chip
                  label={getStatusCount('upcoming')}
                  size="small"
                  sx={{ height: 18, fontSize: '0.7rem' }}
                />
              </Box>
            }
            sx={{ minHeight: 48, textTransform: 'none' }}
          />
          <Tab
            value="completed"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{TOURNAMENT_LIST_TAB_LABELS.completed}</span>
                <Chip
                  label={getStatusCount('completed')}
                  size="small"
                  sx={{ height: 18, fontSize: '0.7rem' }}
                />
              </Box>
            }
            sx={{ minHeight: 48, textTransform: 'none' }}
          />
          {(adminMode || activeTab === 'draft') && (
            <Tab
              value="draft"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{TOURNAMENT_LIST_TAB_LABELS.draft}</span>
                  <Chip label={getStatusCount('draft')} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                </Box>
              }
              sx={{ minHeight: 48, textTransform: 'none', color: 'manage.main' }}
            />
          )}
        </Tabs>
      </Box>

      {/* Tournaments List */}
      <Box sx={{ px: 1, pt: 2 }}>
        {isLoading ? (
          <LoadingState message="Загрузка турниров..." />
        ) : error ? (
          <ErrorState message="Ошибка загрузки турниров" onRetry={refetch} />
        ) : filteredTournaments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <EmojiEvents sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Нет турниров
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {activeTab === 'all' && create.tournament
                ? 'Создайте первый турнир'
                : 'Нет турниров в этой категории'}
            </Typography>
          </Box>
        ) : (
          filteredTournaments.map((tournament) => (
            <TournamentCard
              key={tournament.id}
              tournament={tournament}
              onClick={() => navigate(`/tournaments/${tournament.id}`)}
            />
          ))
        )}
      </Box>

      {create.tournament && (
        <Fab
          color="manage"
          aria-label="Создать турнир"
          sx={{ position: 'fixed', bottom: { xs: `calc(${BOTTOM_NAV_OFFSET} + 16px)`, lg: 24 }, right: 16 }}
          onClick={() => navigate('/tournaments/new')}
        >
          <Add />
        </Fab>
      )}
    </Box>
  );
};
